import delay from 'lodash/delay';
import { useEffect, useRef } from 'react';

const INACTIVITY_DELAY = 1000 * 60 * 60;
const CALLBACK_DELAY = 3000;

interface IUseInactivityProps {
  timeout?: number;
  skip?: boolean;
  callbackDellay?: number;
  inactivityCallback: () => void;
  activityCallback?: () => void;
}

const useInactivity = ({
  timeout = INACTIVITY_DELAY,
  skip = false,
  callbackDellay = CALLBACK_DELAY,
  inactivityCallback,
  activityCallback,
}: IUseInactivityProps): void => {
  const lastActivityRef = useRef(Date.now());
  const inactiveRef = useRef(false);

  useEffect(() => {
    if (skip) return undefined;
    // Function to reset inactivity timer on user interaction
    const resetTimer = () => {
      const wasInactive = inactiveRef.current;
      lastActivityRef.current = Date.now();

      if (wasInactive) {
        inactiveRef.current = false;
        activityCallback?.();
      }
    };
    // List of events to track user activity
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

    events.forEach(event => {
      window.addEventListener(event, resetTimer);
    });

    const checkInactivity = () => {
      if (!inactiveRef.current && Date.now() - lastActivityRef.current > timeout) {
        inactiveRef.current = true;
        delay(() => {
          inactivityCallback();
        }, callbackDellay);
      }
    };

    const intervalId = setInterval(checkInactivity, 1000);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
      clearInterval(intervalId);
    };
  }, [timeout, skip, callbackDellay, inactivityCallback, activityCallback]);
};

export default useInactivity;
